import { ComponentStore } from '@ngrx/component-store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  DEFAULT_PAGINATED_STATE_CONFIG,
  type PaginatedStateConfig,
  PaginatedStateLoadingMode,
  PaginatedStateModel,
} from './paginated-store.types';

/**
 * State-only base class for paginated entity management.
 *
 * Responsibility: entity adapter, selectors, and updaters — no effects, no services.
 * Pair with PaginatedStateEffects for async operations.
 *
 * Subclasses may extend TState beyond PaginatedStoreState&lt;TData&gt; to add custom state.
 *
 * @template TData The data type for items (must have id property)
 * @template TState The full state shape (defaults to PaginatedStoreState&lt;TData&gt;)
 */
export class PaginatedState<
  TData extends { id: string },
  TState extends PaginatedStateModel<TData> = PaginatedStateModel<TData>
> extends ComponentStore<TState> {
  public readonly itemsAdapter: EntityAdapter<TData>;
  public readonly config: Required<PaginatedStateConfig>;

  constructor(config?: Partial<PaginatedStateConfig>, additionalInitialState?: Partial<TState>) {
    const adapter = createEntityAdapter<TData>({ selectId: (item) => item.id });

    const baseState: PaginatedStateModel<TData> = {
      items: adapter.getInitialState(),
      deferredAttributeMemory: {},
      loading: true,
      counting: true,
      searching: false,
      deleting: false,
      error: undefined,
      nextToken: undefined,
      hasMoreItems: true,
      searchTerm: undefined,
      totalCount: 0,
      pageSize: config?.initialPageSize ?? DEFAULT_PAGINATED_STATE_CONFIG.initialPageSize,
      previousPageTokens: [],
    };

    super({ ...baseState, ...additionalInitialState } as TState);

    this.itemsAdapter = adapter;
    this.config = { ...DEFAULT_PAGINATED_STATE_CONFIG, ...config };
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly items$ = this.select((state) => this.itemsAdapter.getSelectors().selectAll(state.items));
  public readonly loading$ = this.select((state) => state.loading);
  public readonly counting$ = this.select((state) => state.counting);
  public readonly searching$ = this.select((state) => state.searching);
  public readonly deleting$ = this.select((state) => state.deleting);
  public readonly error$ = this.select((state) => state.error);
  public readonly nextToken$ = this.select((state) => state.nextToken);
  public readonly hasMoreItems$ = this.select((state) => state.hasMoreItems);
  public readonly searchTerm$ = this.select((state) => state.searchTerm);
  public readonly totalCount$ = this.select((state) => state.totalCount);
  public readonly pageSize$ = this.select((state) => state.pageSize);
  public readonly previousTokens$ = this.select((state) => state.previousPageTokens);
  public readonly deferredAttributeMemory$ = this.select((state) => state.deferredAttributeMemory);
  public readonly itemsLoaded$ = this.select((state) => state.items.ids.length > 0);

  public readonly noItems$ = combineLatest([this.items$, this.totalCount$, this.counting$, this.searching$]).pipe(
    map(([items, totalCount, counting, searching]) => items.length === 0 && totalCount === 0 && !counting && !searching)
  );

  // ============================================================================
  // Updaters
  // ============================================================================

  public readonly setLoading = this.updater((state, loading: boolean) => ({ ...state, loading } as TState));

  public readonly setCounting = this.updater((state, counting: boolean) => ({ ...state, counting } as TState));

  public readonly setSearching = this.updater((state, searching: boolean) => ({ ...state, searching } as TState));

  public readonly setDeleting = this.updater((state, deleting: boolean) => ({ ...state, deleting } as TState));

  public readonly setError = this.updater(
    (state, error: string | undefined) =>
      ({
        ...state,
        error,
        loading: false,
        deleting: false,
      } as TState)
  );

  public readonly setTotalCount = this.updater(
    (state, totalCount: number) =>
      ({
        ...state,
        totalCount,
        counting: false,
      } as TState)
  );

  public readonly updateItems = this.updater((state, { items, lastItem }: { items: TData[]; lastItem?: TData }) => {
    const updatedPreviousTokens = this.updatePreviousTokens(state.previousPageTokens, lastItem);

    const updatedItems =
      this.config.loadingMode === PaginatedStateLoadingMode.CONTINUOUS && lastItem !== undefined
        ? this.itemsAdapter.addMany(items, state.items)
        : this.itemsAdapter.setAll(items, state.items);

    const transformedItems = this.itemsUpdateTransform(updatedItems, state);

    return {
      ...state,
      items: transformedItems,
      hasMoreItems: items.length === state.pageSize,
      previousPageTokens: this.config.loadingMode === 'paginated' ? updatedPreviousTokens : [],
      nextToken: items.length > 0 ? items[items.length - 1] : undefined,
      error: undefined,
      loading: false,
      searching: false,
      deleting: false,
    } as TState;
  });

  /** Seed pagination from an externally-restored position (deep link / saved view). */
  public readonly seedPagination = this.updater(
    (state, seed: { pageSize?: number; previousPageTokens?: (TData | undefined)[] }) =>
      ({
        ...state,
        ...(seed.pageSize != null ? { pageSize: seed.pageSize } : {}),
        ...(seed.previousPageTokens ? { previousPageTokens: seed.previousPageTokens } : {}),
      } as TState)
  );

  public readonly setSearchTerm = this.updater(
    (state, searchTerm: string) =>
      ({
        ...state,
        searchTerm: searchTerm.trim() === '' ? undefined : searchTerm,
        items: this.itemsAdapter.removeAll(state.items),
        hasMoreItems: true,
        previousPageTokens: [],
        nextToken: undefined,
        searching: true,
        loading: true,
      } as TState)
  );

  public readonly changePageSize = this.updater(
    (state, pageSize: number) =>
      ({
        ...state,
        pageSize,
        items: this.itemsAdapter.removeAll(state.items),
        previousPageTokens: [],
        nextToken: undefined,
        searching: false,
        loading: true,
      } as TState)
  );

  public readonly removeItem = this.updater(
    (state, itemId: string) =>
      ({
        ...state,
        items: this.itemsAdapter.removeOne(itemId, state.items),
        deleting: false,
      } as TState)
  );

  public readonly updateItemById = this.updater(
    (state, { id, changes }: { id: string; changes: Partial<TData> }) =>
      ({
        ...state,
        items: this.itemsAdapter.updateOne({ id, changes }, state.items),
      } as TState)
  );

  public readonly storeDeferredAttributes = this.updater(
    (state, { itemId, changes }: { itemId: string; changes: Partial<TData> }) => {
      const currentChanges = state.deferredAttributeMemory[itemId];
      return {
        ...state,
        deferredAttributeMemory: {
          ...state.deferredAttributeMemory,
          [itemId]: { ...currentChanges, ...changes },
        },
      } as TState;
    }
  );

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private updatePreviousTokens(
    previousTokens: (TData | undefined)[],
    nextToken?: TData | undefined
  ): (TData | undefined)[] {
    const nextTokenIndex = previousTokens.findIndex((token) => token?.id === nextToken?.id);
    return nextTokenIndex > -1 ? previousTokens.slice(0, nextTokenIndex + 1) : [...previousTokens, nextToken];
  }

  // ============================================================================
  // Overridable Hook
  // ============================================================================

  /**
   * Override in subclasses to sort/filter items before state updates are confirmed.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected itemsUpdateTransform(items: EntityState<TData>, state: TState): EntityState<TData> {
    return items;
  }
}
